import { AppShell, Badge, Button, Group, Image, NavLink, ScrollArea, Text } from '@mantine/core';
import { useSahaGunu } from '../api/queries';
import { CalismaSaatiScreen } from '../screens/CalismaSaatiScreen';
import { NavLink as RouterNavLink, Navigate, Route, Routes } from 'react-router';
import type { TenantOverview } from '@isg/shared-types';
import { SAPKA_ETIKET } from '../i18n/tr';
import { PortalFirmaScreen } from '../screens/PortalFirmaScreen';
import { PortalPersonelScreen } from '../screens/PortalPersonelScreen';
import { PortalYuklemeScreen } from '../screens/PortalYuklemeScreen';
import {
  BANT_IC_PAY,
  BANT_LOGO_SOL_KAYMA_PX,
  BANT_LOGO_YUKSEKLIK_PX,
  BANT_YUKSEKLIK_PX,
  KABUK_KIRILMA,
  MARKA,
  MENU_DIS_PAY,
  MENU_GENISLIK_PX,
} from '../theme';
import {
  CekmeceAlti,
  CekmeceBaglami,
  MenuBurger,
  useKabukOlcusu,
  useMenuCekmecesi,
} from './dar-ekran';

/**
 * YÜKLENİCİ PORTALI kabuğu (F1.5·S12 — Kapsam §18.2): temsilci ANA UYGULAMAYI
 * GÖRMEZ — girişte bu dar kabuk açılır (ana menü/rotalar hiç mount edilmez).
 * Bu bir yetki kararı DEĞİLDİR: her uç kendi guard'ından geçer; kabuk yalnız
 * temsilcinin yüzeyini üç ekrana indirir (firma + personel + yükleme).
 */
const PORTAL_MENU = [
  { path: '/', label: 'Firmam' },
  { path: '/personel', label: 'Personel' },
  { path: '/personel-yukleme', label: 'Personel yükleme' },
  // F3B·S2 (U9): temsilci KENDİ firmasının çalışma saatini girer — firma
  // oturumdan türetilir, liste yalnız kendi firmasının satırları (D2).
  { path: '/calisma-saatleri', label: 'Çalışma saatleri' },
] as const;

export function PortalShellLayout({
  tenant,
  onLogout,
}: {
  tenant: TenantOverview;
  onLogout: () => void;
}): React.JSX.Element {
  // Madde 302: saha günü portal kabuğunda da ÖN YÜKLENİR (tek kaynak).
  useSahaGunu();
  // ⚠️ DAR EKRAN DAVRANIŞI ANA KABUKLA ORTAKTIR (`dar-ekran.tsx`): kırılma
  // noktası, çekmece ve sadeleşme sırası tek yerde yaşar. Portalın kendi
  // kopyası YOKTUR — bugüne dek `breakpoint: 'xs'` iki dosyada ayrı ayrı
  // yazılıydı ve kopya sessizce ayrışabilirdi (§11 · §2.31a deseni).
  const olcu = useKabukOlcusu();
  const cekmece = useMenuCekmecesi(olcu.darEkran);

  const holdingRozeti = <Badge variant="light">{tenant.ad}</Badge>;
  const rolEtiketi = (
    <Text size="sm" c="dimmed" data-testid="sapka-etiketi">
      {SAPKA_ETIKET.contractor_rep}
    </Text>
  );
  const cikisDugmesi = (
    <Button
      data-testid="sso-logout"
      size="xs"
      variant="default"
      onClick={onLogout}
      fullWidth={olcu.rolCekmecede}
    >
      Çıkış yap
    </Button>
  );

  return (
    <AppShell
      header={{ height: BANT_YUKSEKLIK_PX }}
      navbar={{
        width: MENU_GENISLIK_PX,
        breakpoint: KABUK_KIRILMA,
        collapsed: { mobile: !cekmece.acik },
      }}
      padding="md"
    >
      <AppShell.Header px={BANT_IC_PAY}>
        {/* Bant TEK YÜZEYDİR (G4): portal kabuğu ana kabukla aynı zemini, aynı
            yüksekliği ve aynı logoyu kullanır — CSS kuralı zaten ortak. */}
        <Group h="100%" justify="space-between" wrap="nowrap">
          {/* ✏️ 19.09.2026 — SOL GRUPTA NEFES: bant iki katına çıkınca eski `sm`
              aralığı dar kaldı; logo ile tenant rozeti bitişik duruyordu. Değer
              boşluk ÖLÇEĞİNDEN gelir (G5 — ekranda px yazılmaz). */}
          <Group gap="sm" wrap="nowrap">
            {olcu.darEkran && <MenuBurger cekmece={cekmece} />}
            <Group gap="lg" wrap="nowrap">
              <Image
                src={MARKA.logo}
                alt={MARKA.urunAdi}
                h={BANT_LOGO_YUKSEKLIK_PX}
                w="auto"
                fit="contain"
                // Sol hiza: logonun sol kenarı menü METNİYLE aynı çizgide. Bandın
                // kendi iç payı sağdaki düğmelerle ortak olduğu için değişmez;
                // farkı yalnız logo kapatır (değer theme.ts'te türetilir).
                ml={BANT_LOGO_SOL_KAYMA_PX}
                data-testid="marka-logosu"
              />
              {/* ⚠️ PORTAL ROZETİ DAR EKRANDA DA BANTTA KALIR: *"hangi
                  yüzeydeyim"* sorusunun cevabıdır ve temsilcinin ana kabuğu
                  hiç görmediği düşünülürse tek işarettir (§18.2). Taşınan,
                  bir alt öncelikteki HOLDİNG rozetidir. */}
              <Badge variant="filled" data-testid="portal-rozeti">
                Yüklenici Portalı
              </Badge>
              {!olcu.cokDar && holdingRozeti}
            </Group>
          </Group>
          <Group gap="sm" wrap="nowrap">
            {!olcu.rolCekmecede && rolEtiketi}
            {!olcu.rolCekmecede && cikisDugmesi}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        p={MENU_DIS_PAY}
        data-testid="portal-menu"
        ref={cekmece.navbarRef}
        // ⚠️ KAPALI ÇEKMECE EKRANDAN ÇIKAR, DOM'DAN ÇIKMAZ: Mantine onu
        // `translateX(-100%)` ile dışarı alır, yani görünmez ama
        // KLAVYEYLE SEKMELENEBİLİR kalır — kullanıcı odağı kaybeder ve
        // ekranda hiçbir şey olmaz (§2.40'ın "görünmüyor ama çalışıyor"
        // tersi). `inert` odağı da tıklamayı da kapatır.
        inert={olcu.darEkran && !cekmece.acik}
      >
        {/* ⚠️ ÇEKMECENİN ÜSTÜ BANDIN EŞİĞİNİ OKUR, MENÜNÜN EŞİĞİNİ DEĞİL:
            `darEkran`a bağlansaydı 820px'te rol etiketi HEM BANTTA HEM
            ÇEKMECEDE çizilirdi — aynı `data-testid`den iki düğüm, yani
            `dar-ekran.tsx` başlığındaki tuzağın ta kendisi. Rozet koşulu
            ayrıca yazılır ama kapsanır (cokDar ⊂ rolCekmecede). */}
        {olcu.rolCekmecede && (
          <CekmeceBaglami>
            {olcu.cokDar && holdingRozeti}
            {rolEtiketi}
          </CekmeceBaglami>
        )}
        <ScrollArea type="auto" style={{ flex: 1, minHeight: 0 }}>
          {PORTAL_MENU.map((item) => (
            <NavLink
              key={item.path}
              component={RouterNavLink}
              to={item.path}
              end={item.path === '/'}
              label={item.label}
              onClick={cekmece.kapat}
            />
          ))}
        </ScrollArea>
        {olcu.rolCekmecede && <CekmeceAlti>{cikisDugmesi}</CekmeceAlti>}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<PortalFirmaScreen />} />
          <Route path="/personel" element={<PortalPersonelScreen />} />
          <Route path="/personel-yukleme" element={<PortalYuklemeScreen />} />
          <Route path="/calisma-saatleri" element={<CalismaSaatiScreen portal />} />
          {/* Ana uygulamanın rotaları portalda YOK — bilinmeyen yol firma sayfasına döner. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
